import { Box, Button, DialogActions, Stack, Typography } from '@mui/material';
import colors from 'common/constants/colors';
import PropTypes from 'prop-types';

/**
 * POM-6010 — the review dialog's bottom bar.
 *
 * States what the batch will actually do, then offers the controls to do it. The count line is the
 * honest summary the user prints against: "24 of 25 labels will print · 1 excluded" rather than a
 * bare Print button that quietly drops one.
 *
 * The print controls themselves are supplied by the caller (`renderPrintActions`), because their
 * enablement has two owners: whether the batch has settled — which this dialog knows — and whether
 * the PDF exists and the user may mark printed, which only PrintLabelButton knows. `locked` is this
 * half of that rule.
 *
 * `busy` is the weaker half of the same signal, for controls that do not need a finished batch:
 * Regenerate rebuilds the labels rather than consuming them, so it stays available while excluded
 * rows sit unacknowledged and only goes dead while something is actually in flight.
 *
 * Three regions, spaced apart: the count on the left, `renderPrintNotice` in the middle, the
 * controls on the right. The notice is a prompt carrying its own button — the regenerate alert —
 * and it lives here rather than in the body above because the sentence is about the labels Download
 * and Print are for. Up there it was one more banner to scroll past, and the button it names was
 * five controls away; jammed against Download it just crowded them.
 */
const BulkLotNdcPrintBar = ({
  counts,
  excludedCount,
  helperText,
  printLocked,
  actionsBusy,
  committing,
  renderPrintNotice,
  renderPrintActions,
  onClose,
}) => (
  <DialogActions
    sx={{
      px: 3,
      py: 2,
      borderTop: '1px solid',
      borderColor: 'divider',
      // space-between rather than DialogActions' own flex-end: it is what puts the notice in the
      // middle of the row instead of leaving it pressed against the first button.
      justifyContent: 'space-between',
      gap: 2,
      // Seven controls plus a prompt: on a narrow viewport this has to fold rather than overflow.
      flexWrap: 'wrap',
      rowGap: 1.5,
    }}
  >
    {/* minWidth: 0 so the count line yields first when space runs short — it is the one thing
        here that reflows instead of being clipped. */}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant='body2' sx={{ fontWeight: 600 }}>
        {`${counts.total - excludedCount} of ${counts.total} labels will print`}
        {/*
          Two different things to say, and saying the wrong one is how this line stops being
          trusted. Rows dropped by the user are "excluded"; rows printing with a lot that may be
          wrong are still printing, so calling them excluded would be a lie about the output.
        */}
        {excludedCount > 0 && (
          <Box component='span' sx={{ color: colors.warningAmberText, fontWeight: 600 }}>
            {` · ${excludedCount} excluded`}
          </Box>
        )}
        {excludedCount === 0 && counts.blocked > 0 && (
          <Box component='span' sx={{ color: colors.warningAmberText, fontWeight: 600 }}>
            {` · ${counts.blocked} need${counts.blocked > 1 ? '' : 's'} attention`}
          </Box>
        )}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {helperText}
      </Typography>
    </Box>
    <Stack direction='row' spacing={1} alignItems='center' sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      {renderPrintNotice({ busy: actionsBusy })}
      {renderPrintActions({ locked: printLocked, busy: actionsBusy })}
      {/* Close routes back through the dialog's guard, which prompts if edits are uncommitted. */}
      <Button onClick={onClose} variant='outlined' color='secondary' disabled={committing}>
        Close
      </Button>
    </Stack>
  </DialogActions>
);

BulkLotNdcPrintBar.propTypes = {
  counts: PropTypes.shape({
    printable: PropTypes.number,
    total: PropTypes.number,
    blocked: PropTypes.number,
  }).isRequired,
  /** How many rows the user has opted to drop — 0 when the whole batch is printing. */
  excludedCount: PropTypes.number,
  helperText: PropTypes.string.isRequired,
  printLocked: PropTypes.bool.isRequired,
  actionsBusy: PropTypes.bool.isRequired,
  committing: PropTypes.bool.isRequired,
  renderPrintNotice: PropTypes.func,
  renderPrintActions: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

BulkLotNdcPrintBar.defaultProps = {
  excludedCount: 0,
  renderPrintNotice: () => null,
};

export default BulkLotNdcPrintBar;
